import { Alert, Button } from '@heroui/react'
import { getAuthInstance } from '@zealth/core/firebase/runtime'
import { signOut } from 'firebase/auth'

import { useInsightsAccount } from '../auth/InsightsAccountContext'
import { describePayloadError } from '../payload/payloadError'
import { PeriodProvider } from '../payload/PeriodContext'
import { useInsightsPayload } from '../payload/useInsightsPayload'
import { CardHead } from './components'
import Freshness from './Freshness'
import PeriodPicker from './PeriodPicker'
import Sections from './sections'

const PAGE_INFO = [
  'Accounts and saved financial data from the product itself, alongside website and app activity. Nothing from staging or test environments.',
  'The two halves are deliberately kept apart: what people saved is exact, what they browsed is approximate, and mixing them is how you get a confident wrong answer. No figure on this page divides one by the other.',
  'Every figure follows the selected period, including the numbers inside the notes — so nothing here can go stale or disagree with the chart above it.',
]

const DashboardPage = () => {
  const { email } = useInsightsAccount()
  const payload = useInsightsPayload()

  return (
    <main className="mx-auto grid w-full max-w-6xl gap-6 px-4 py-6">
      <div className="flex flex-wrap items-baseline gap-4">
        <h1 className="flex-1 text-2xl font-semibold tracking-tight">
          Zealth — how the product is doing
        </h1>
        <span className="text-muted text-xs">{email}</span>
        <Button
          size="sm"
          variant="ghost"
          onPress={() => signOut(getAuthInstance())}
        >
          Sign out
        </Button>
      </div>

      <CardHead
        // The prototype said "Real customers only"; the page also shows anonymous
        // traffic and, to admins, the team — so it says what it does instead.
        title="Live product data. Customers kept apart from the team."
        info={PAGE_INFO}
      />

      {payload.isPending && (
        <p className="text-sm">Loading the latest numbers…</p>
      )}

      {payload.isError && (
        <Alert status="danger">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Description>
              {describePayloadError(payload.error)}
            </Alert.Description>
          </Alert.Content>
        </Alert>
      )}

      {payload.data && (
        <PeriodProvider panels={payload.data.panels}>
          <Freshness generatedAt={payload.data.generatedAt} />
          <PeriodPicker />
          <Sections />
        </PeriodProvider>
      )}
    </main>
  )
}

export default DashboardPage
